import { oordeelVoorNorm, verschilMetNorm } from './normen';
import { bouwTabelRegels, teltMeeInRangschikking, type TabelRegel } from './tabel';
import type { StapKeuze } from './stapFilter';
import type { HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 33.2 — het rekenwerk onder de aandachtslijst.
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `kerncijfers.ts`,
 * `tabel.ts` en `stapFilter.ts`: de scherpste regels van deze story zijn rekenregels, en die horen
 * navraagbaar op één plek te staan.
 *
 * > [!note] Wat sturen oplevert is het verschil met de norm KEER het aantal stuks
 * > `kost = (gemiddelde per stuk − norm) × stuks`, berekend binnen de gekozen stap.
 *
 * Dat is een ándere volgorde dan op snelheid, en dat is het hele punt van deze story. Gemeten op
 * productie op 29 augustus 2026 met 18,0 als voorbeeldnorm: Sita 22,4 per stuk over 13 stuks kost
 * 57 minuten, Lisette 19,5 over 35 stuks kost er 51. Lisette is van de twee de SNELSTE, en zou op
 * een ranglijst op snelheid helemaal niet opvallen.
 *
 * DRIE VALLEN die hier met opzet dichtgezet zijn:
 *
 * 1. `oordeelVoorNorm` beslist wie erin staat; `verschilMetNorm` levert alleen de grootte. Die twee
 *    lopen uiteen bij drijvendekommaruis — zie `hoortInDeLijst` hieronder.
 * 2. Er wordt gerekend met het ONAFGERONDE gemiddelde. Afronden gebeurt pas bij het tonen; een kost
 *    van 57 minuten mag niet ontstaan of verdwijnen door de decimaal die in beeld staat (AC-12).
 * 3. Zonder norm is er geen lijst, geen nul en geen streepje. Een norm van nul is geen norm maar
 *    een fout, en telt hier dus als "niet gezet" — niet als een norm waar iedereen boven zit.
 */

/** Eén medewerker die in de gekozen stap boven de norm zit. */
export interface AandachtsRegel {
  userId: number;
  /** Alleen aanwezig bij `named`. Nooit invullen met een verzonnen waarde. */
  userName?: string;
  userNameOutcome: string;
  /** Het ONAFGERONDE gemiddelde per stuk in deze stap. Afronden gebeurt pas bij het tonen. */
  gemiddeldePerStuk: number;
  /** Hoeveel minuten per stuk erboven, uit `verschilMetNorm`. Altijd groter dan nul. */
  bovenDeNorm: number;
  /** `handlings[].tradeItems` van DÍE stap — binnen één stap is dat een aantal stuks. */
  stuks: number;
  /** `(gemiddelde − norm) × stuks`, in minuten over de hele periode. */
  kost: number;
  /** Of de grafiek op deze regel gezet mag worden. Zie `aanklikbaar` in `tabel.ts`. */
  aanklikbaar: boolean;
}

export interface Aandachtslijst {
  /** De norm van deze stap, of `null` zodra er geen is. `null` betekent: toon de uitleg (AC-7). */
  norm: number | null;
  /** Wie boven de norm zit, gerangschikt op kost, aflopend. */
  regels: AandachtsRegel[];
  /** Hoeveel medewerkers op of onder de norm zitten. Die kosten niets en staan er niet in (AC-4). */
  opOfOnderNorm: number;
  /** Hoeveel medewerkers minuten droegen maar geen stuk afrondden; die hebben geen gemiddelde (AC-14). */
  zonderStuks: number;
}

const LEEG: Aandachtslijst = { norm: null, regels: [], opOfOnderNorm: 0, zonderStuks: 0 };

/**
 * Of deze meting boven de norm ligt.
 *
 * HET OORDEEL BESLIST, en dat is AC-4a. `oordeelVoorNorm` rondt de grens op zes decimalen af,
 * `verschilMetNorm` vergelijkt rauw op `verschil === 0`. Een deling die op 4,1000000000000005
 * uitkomt tegen een norm van 4,1 is dus `groen` én tegelijk `boven` — 12,3 minuten over 3 stuks
 * levert precies dat op. Zou het verschil beslissen, dan stond die persoon in de lijst met een kost
 * van een miljardste minuut terwijl de tabel ernaast hem groen kleurt.
 *
 * `geen` kan hier niet voorkomen: zowel de waarde als de norm zijn hierboven al vastgesteld.
 */
function hoortInDeLijst(gemiddelde: number, norm: number): boolean {
  return oordeelVoorNorm(gemiddelde, norm) !== 'groen';
}

/** De naam mee overnemen zonder hem te verzinnen: alleen wat de regel zelf draagt. */
function naamVelden(regel: TabelRegel): { userName?: string } {
  return regel.userName !== undefined ? { userName: regel.userName } : {};
}

/**
 * De aandachtslijst van één stap, tegen de norm van díe stap.
 *
 * Op "Beide" wordt deze functie twee keer aangeroepen, elk met zijn eigen norm — nooit één lijst
 * waarin twee normen door elkaar lopen (AC-5).
 *
 * De regels komen uit `bouwTabelRegels`, en dat is geen omweg maar de reden dat de lijst en de
 * tabel niet uit elkaar kunnen lopen: het gemiddelde per stuk wordt op één plek gedeeld.
 */
export function bouwAandachtslijst(
  rijen: HandlingTimePerPersonRow[],
  stap: StapKeuze,
  norm: number | null,
): Aandachtslijst {
  // Een norm van nul is geen norm maar een fout; de serverlaag keurt hem af, en hier telt hij als
  // "niet gezet" (AC-7 en het randgeval "norm van 0").
  if (norm === null || !(norm > 0)) return LEEG;

  const regels: AandachtsRegel[] = [];
  let opOfOnderNorm = 0;
  let zonderStuks = 0;

  for (const regel of bouwTabelRegels(rijen)) {
    // `person-removed` en `automated-system` zijn geen collega's, en op geen van beide valt te
    // sturen (AC-15). Ze tellen ook in de gétallen niet mee: "twee medewerkers zitten op of onder
    // de norm" mag niet half over een geautomatiseerde melding gaan. Dezelfde uitzondering als bij
    // de rangschikking van story 31.6, en met opzet dezelfde functie.
    if (!teltMeeInRangschikking(regel)) continue;

    // `behandelingen` heet in `tabel.ts` zo omdat het OVER DE STAPPEN HEEN geen stuks zijn: een
    // stuk dat door beide stappen ging telt daar twee keer. BINNEN één stap — en dat is alles wat
    // hier gebeurt — is het wél een aantal stuks, en zo heet het in de lijst dan ook (AC-1).
    const { minuten, behandelingen, gemiddeldePerStuk } = regel[stap];

    if (gemiddeldePerStuk === null || behandelingen === null || behandelingen <= 0) {
      // WIE MINUTEN DROEG MAAR GEEN STUK AFRONDDE heeft geen gemiddelde per stuk, en wordt genoemd
      // met zijn eigen reden (AC-14).
      //
      // ALLEEN WIE WERKELIJK TIJD DROEG, en dat is een codereviewbevinding van 30 augustus 2026.
      // Het stond hier als `minuten === null`, en een stap met nul minuten én nul stuks kwam er dan
      // doorheen als "droeg wel tijd maar rondde geen stuk af" — een bewering over iemand die niets
      // deed. De bron levert `null` en nooit `0`, dus dit geval hoort niet te bestaan; het is de
      // soort onwaarheid die je niet aan een belofte van de bron wilt ophangen.
      //
      // WIE IN DEZE STAP NIETS DEED wordt hier dus nergens geteld, en dat is met opzet: er valt over
      // hem in deze stap niets te zeggen. Datzelfde geldt voor het spiegelbeeld — stuks zonder
      // minuten. Die kan bij deze bron niet ontstaan, en zou hij ontstaan, dan is "rondde geen stuk
      // af" precies de verkeerde reden om erbij te zetten.
      if (minuten !== null && minuten > 0) zonderStuks += 1;
      continue;
    }

    if (!hoortInDeLijst(gemiddeldePerStuk, norm)) {
      opOfOnderNorm += 1;
      continue;
    }

    // `minuten` is hier nooit `null`, en dat is nagerekend en geen aanname: afronden op zes
    // decimalen is monotoon, dus uit "het oordeel is niet groen" volgt dat het gemiddelde ook RAUW
    // boven de norm ligt, en dan geeft `verschilMetNorm` de soort `boven` met een getal erbij.
    // De `??` is dus dode code; hij staat er zodat een latere wijziging aan `verschilMetNorm` hier
    // geen `null` in een vermenigvuldiging duwt. Wat hij NIET vangt is `NaN` — die zou hier een
    // onbepaalde sortering opleveren, en juist daarom mag dit de enige plek blijven waar het
    // verschil vandaan komt.
    const bovenDeNorm = verschilMetNorm(gemiddeldePerStuk, norm).minuten ?? 0;

    regels.push({
      userId: regel.userId,
      ...naamVelden(regel),
      userNameOutcome: regel.userNameOutcome,
      gemiddeldePerStuk,
      bovenDeNorm,
      stuks: behandelingen,
      // LETTERLIJK ZOALS DE STORY HET SCHRIJFT: (gemiddelde − norm) × stuks, met het onafgeronde
      // gemiddelde (AC-12).
      //
      // `minuten − norm × stuks` zou op papier hetzelfde getal geven, maar NIET in drijvende komma,
      // en dat is een codereviewbevinding van 30 augustus 2026: 1,4 minuut over 3 stuks tegen een
      // norm van 0,3 hoort precies 0,5 te kosten en levert hier 0.4999999999999999 op. Béide
      // volgordes hebben zulke grensgevallen, dus dit is geen verkeerde formule maar een
      // nauwkeurigheidsrestje — en het wordt opgevangen waar het schade doet: het scherm rondt de
      // kost op zes decimalen af vóór het de grens van een halve minuut beslecht.
      kost: bovenDeNorm * behandelingen,
      aanklikbaar: regel.aanklikbaar,
    });
  }

  // OP KOST, AFLOPEND (AC-2). Bij een gelijke kost beslist het gebruikersnummer, zodat de volgorde
  // vast en herhaalbaar is: een lijst die bij elke weergave anders staat is niet te bespreken.
  regels.sort((a, b) => (b.kost - a.kost) || (a.userId - b.userId));

  return { norm, regels, opOfOnderNorm, zonderStuks };
}
